import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AnimatedSection } from '@/components/AnimatedSection';
import { useSEO } from '@/hooks/useSEO';
import { ArrowRight, Lock, CheckCircle, Phone, MessageSquare, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const suggestedQuestions = [
  'Can I deduct my home office?',
  'What do I do about this IRS notice?',
  'How do estimated tax payments work?',
  'Can I write this off?',
  'What happens if I have unfiled returns?',
  'Am I in trouble with the IRS?',
];

const exampleQuestions = [
  'What do I do about this IRS notice?',
  'Can I write this off?',
  'Why do I owe?',
  'What happens if I have unfiled returns?',
  'Am I in trouble?',
  'Is it too late to fix a past mistake?',
];

export default function AskQuestion() {
  useSEO({
    title: 'Ask a Tax Question - Free',
    description: 'Ask a real tax question and get free guidance from an experienced tax professional. Receive a one-on-one consultation with clear next steps, and if you need further assistance, we will provide a fixed upfront fee.',
  });

  const [form, setForm] = useState({ name: '', email: '', phone: '', question: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // AI instant answer state
  const [aiQuestion, setAiQuestion] = useState('');
  const [aiAnswer, setAiAnswer] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  const handleAiAsk = async (question) => {
    const q = question || aiQuestion;
    if (!q.trim()) return;
    setAiQuestion(q);
    setAiLoading(true);
    setAiAnswer('');
    try {
      const res = await axios.post(`${API}/ai/ask`, { question: q });
      setAiAnswer(res.data.answer);
    } catch {
      setAiAnswer('We couldn\'t process your question right now. Please try again or call us at 855-589-2461 for immediate help.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.question) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, type: 'tax_question' });
      setSubmitted(true);
      toast.success('Your question has been submitted. We will be in touch soon.');
    } catch {
      toast.error('Something went wrong. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="ask-question-page">
      {/* Hero */}
      <section className="bg-brand-navy py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="max-w-2xl">
            <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight leading-none text-white mb-5">
              Ask a Tax Question - Free
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed">
              Ask a real tax question and get free guidance from an experienced tax professional. Receive a one-on-one consultation with clear next steps, and if you need further assistance, we will provide a fixed upfront fee.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* AI Instant Answer Section */}
      <section data-testid="ai-instant-answer-section" className="py-16 lg:py-20 bg-brand-muted border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6">
          <AnimatedSection>
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-brand-green/10 rounded-full text-xs font-semibold uppercase tracking-widest text-brand-green mb-4">
                <Sparkles className="w-3.5 h-3.5" /> Tax Assistant
              </div>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-3">
                Get Instant Answers to Your Tax Questions
              </h2>
              <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] max-w-xl mx-auto">
                Powered by 25+ years of tax expertise. Ask about deductions, IRS notices, filing requirements, and more.
              </p>
            </div>

            {/* Suggested Questions */}
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              {suggestedQuestions.map((q, i) => (
                <button
                  key={i}
                  data-testid={`ai-suggested-${i}`}
                  onClick={() => { setAiQuestion(q); handleAiAsk(q); }}
                  className="px-3 py-1.5 text-xs font-medium bg-white border border-slate-200 rounded-full text-slate-600 hover:border-brand-green hover:text-brand-green transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>

            {/* Question Input */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Textarea
                data-testid="ai-question-input"
                value={aiQuestion}
                onChange={(e) => setAiQuestion(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAiAsk(); } }}
                placeholder="Type your tax question here..."
                rows={2}
                className="bg-white resize-none flex-1 text-base"
              />
              <Button
                data-testid="ai-ask-btn"
                onClick={() => handleAiAsk()}
                disabled={aiLoading || !aiQuestion.trim()}
                className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold px-6 py-4 sm:py-0 rounded-md sm:self-end w-full sm:w-auto"
              >
                {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Get Answer'}
              </Button>
            </div>

            <p className="text-xs text-slate-400 mt-3 text-center">
              This AI provides general tax information. It is not a substitute for professional tax advice. Always verify with a qualified tax professional for your specific situation.
            </p>

            {/* AI Answer Display */}
            {(aiLoading || aiAnswer) && (
              <div data-testid="ai-answer-display" className="mt-8 bg-white border border-slate-200 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-4 h-4 text-brand-green" />
                  <span className="text-xs font-semibold uppercase tracking-widest text-brand-green">TaxPro - Answers</span>
                </div>
                {aiLoading ? (
                  <div className="flex items-center gap-3 text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Analyzing your question...</span>
                  </div>
                ) : (
                  <div className="prose prose-sm max-w-none">
                    {aiAnswer.split('\n').map((paragraph, i) => (
                      paragraph.trim() ? (
                        <p key={i} className="text-[#222222] text-sm leading-relaxed mb-3">
                          {paragraph}
                        </p>
                      ) : null
                    ))}
                    <div className="mt-6 pt-4 border-t border-slate-100">
                      <p className="text-xs text-slate-400 mb-3">
                        Need personalized guidance for your specific situation?
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <a href="https://taxproanswers.setmore.com" target="_blank" rel="noopener noreferrer">
                          <Button size="sm" className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold rounded-md text-xs">
                            Book a Consultation <ArrowRight className="w-3 h-3 ml-1" />
                          </Button>
                        </a>
                        <a href="tel:8555892461">
                          <Button size="sm" variant="outline" className="border-brand-navy text-brand-navy font-semibold rounded-md text-xs">
                            <Phone className="w-3 h-3 mr-1" /> 855-589-2461
                          </Button>
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </AnimatedSection>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left: empty */}
            <AnimatedSection>

            {/* Right: Form */}
            </AnimatedSection>
            <AnimatedSection delay={0.15}>
              {submitted ? (
                <div data-testid="ask-success-message" className="bg-brand-muted border border-brand-green/20 rounded-lg p-10 text-center">
                  <CheckCircle className="w-12 h-12 text-brand-green mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-xl text-brand-navy mb-3">
                    Question Received
                  </h3>
                  <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] mb-6">
                    We've received your question and will respond the same business day.
                  </p>
                  <Link to="/">
                    <Button variant="outline" className="border-brand-navy text-brand-navy">
                      Back to Home
                    </Button>
                  </Link>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-testid="ask-question-form"
                  className="bg-brand-muted border border-slate-200 rounded-lg p-8"
                >
                  <h3 className="font-heading font-semibold text-lg text-brand-navy mb-6">
                    Submit Your Question
                  </h3>
                  <div className="space-y-5">
                    <div>
                      <Label htmlFor="name" className="text-sm font-medium text-slate-700 mb-1.5 block">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        data-testid="ask-input-name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="John Doe"
                        className="bg-white"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-sm font-medium text-slate-700 mb-1.5 block">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        data-testid="ask-input-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="john@example.com"
                        className="bg-white"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-sm font-medium text-slate-700 mb-1.5 block">
                        Phone Number (Optional)
                      </Label>
                      <Input
                        id="phone"
                        data-testid="ask-input-phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                        className="bg-white"
                      />
                    </div>
                    <div>
                      <Label htmlFor="question" className="text-sm font-medium text-slate-700 mb-1.5 block">
                        Your Tax Question *
                      </Label>
                      <Textarea
                        id="question"
                        data-testid="ask-input-question"
                        value={form.question}
                        onChange={(e) => setForm({ ...form, question: e.target.value })}
                        placeholder="Describe your tax question or concern..."
                        rows={5}
                        className="bg-white resize-none"
                        required
                      />
                    </div>
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="consent"
                        data-testid="ask-consent-checkbox"
                        required
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-green focus:ring-brand-green flex-shrink-0"
                      />
                      <label htmlFor="consent" className="text-xs text-slate-500 leading-relaxed">
                        By clicking "Submit" I agree to receive emails, GENERAL COMMUNICATIONS, CUSTOMER SUPPORT, and MARKETING text messages, and phone calls from TAXPROS. I also agree to the Terms of Use and Privacy Policy linked below. Reply STOP to stop and HELP for help for more information. Message &amp; data rates may apply. Message frequency varies. <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand-green hover:underline font-medium">Privacy Policy</a>.
                      </label>
                    </div>
                    <Button
                      disabled={submitting}
                      className="w-full bg-brand-green hover:bg-brand-green-hover text-white font-semibold py-6 text-base rounded-md"
                    >
                      {submitting ? 'Submitting...' : 'Submit Your Question'}
                      {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
                    </Button>
                  </div>
                </form>
              )}
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
}
